import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { TUploadedFile } from '@onesource/schemas';
import { memo, useMemo } from 'react';
import { MdCheckCircle, MdImage, MdZoomIn } from 'react-icons/md';
import { CopyUrlMenu } from './copy-url-menu';
import { MediaThumb } from './media-thumb';

export type TMediaThumbnailProps = {
  file: TUploadedFile;
  isSelected: boolean;
  onSelect: (file: TUploadedFile) => void;
  onPreview: (file: TUploadedFile) => void;
};

export const MediaThumbnail = memo(function MediaThumbnail(props: TMediaThumbnailProps) {
  const { file, isSelected, onSelect, onPreview } = props;
  const theme = useTheme();
  const primaryMain = theme.palette.primary.main;

  const overlayBtnSx = useMemo(
    () => ({
      p: '6px',
      bgcolor: 'rgba(255,255,255,0.95)',
      color: 'text.primary',
      boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}`,
      transition: 'transform 0.1s',
      '&:hover': { bgcolor: 'white', transform: 'scale(1.1)' },
    }),
    [theme.palette.common.black],
  );

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2,
        overflow: 'hidden',
        cursor: 'pointer',
        border: `2.5px solid ${isSelected ? primaryMain : 'transparent'}`,
        boxShadow: isSelected
          ? `0 0 0 1px ${primaryMain}, 0 6px 18px ${alpha(primaryMain, 0.3)}`
          : `0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
        transition: 'border-color 0.1s, box-shadow 0.1s',
        bgcolor: theme.palette.grey[100],
        '&:hover': {
          border: `2.5px solid ${isSelected ? primaryMain : alpha(primaryMain, 0.45)}`,
          boxShadow: isSelected
            ? `0 0 0 1px ${primaryMain}, 0 6px 18px ${alpha(primaryMain, 0.3)}`
            : `0 6px 18px ${alpha(theme.palette.common.black, 0.18)}`,
          '& .thumb-overlay': {
            opacity: 1,
            pointerEvents: 'auto',
          },
        },
      }}
      onClick={() => onSelect(file)}
    >
      {/* Image */}
      <Box
        sx={{
          width: '100%',
          aspectRatio: '1/1',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ width: '100%', height: '100%' }}>
          {file.publicUrl ? (
            <MediaThumb
              src={file.publicUrl}
              alt={file.filename}
              size={32}
              fallbackColor={theme.palette.action.disabled}
            />
          ) : (
            <MdImage size={32} color={theme.palette.action.disabled} />
          )}
        </Box>
      </Box>

      {/* Hover overlay — CSS-driven, no React state */}
      {file.publicUrl && (
        <Box
          className="thumb-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.35)} 0%, transparent 50%)`,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-end',
            gap: 1,
            p: 0.75,
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.1s ease',
          }}
        >
          <Tooltip title="Preview" placement="top" arrow>
            <IconButton
              size="medium"
              sx={overlayBtnSx}
              onClick={e => {
                e.stopPropagation();
                onPreview(file);
              }}
            >
              <MdZoomIn size={20} />
            </IconButton>
          </Tooltip>
          {file.cdnUrls && (
            <Box onClick={e => e.stopPropagation()} sx={{ lineHeight: 0 }}>
              <CopyUrlMenu cdnUrls={file.cdnUrls} variantSizes={{}} standalone iconSize={20} buttonSx={overlayBtnSx} />
            </Box>
          )}
        </Box>
      )}

      {/* Selected badge */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(primaryMain, 0.25),
          }}
        >
          <Box
            sx={{
              color: primaryMain,
              bgcolor: 'background.paper',
              borderRadius: '50%',
              lineHeight: 0,
              boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.25)}`,
            }}
          >
            <MdCheckCircle size={36} />
          </Box>
        </Box>
      )}

      {/* Filename strip */}
      <Tooltip title={file.filename} placement="top" arrow>
        <Box
          sx={{
            px: 1,
            py: 0.75,
            bgcolor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
            {file.filename}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
});
